import { entraPorMagicLink, type Rol } from '@pasos/schemas';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router';
import { AvisoDeDemostracion } from '../components/domain/AvisoDeDemostracion.js';
import {
  Cargando,
  ErrorDeCarga,
  SesionExpirada,
  SinConexion,
  SinPermiso,
} from '../components/ui/Estado.js';
import { SISTEMA } from '../copy/index.js';
import { ErrorDeApi, ErrorDeRed, obtener, renovarConDispositivo } from '../lib/api.js';
import { leerSesion } from '../lib/sesion.js';

/**
 * Quién está usando la app · y qué puede ver.
 *
 * El rol sale de `GET /api/v1/yo`, que lo lee **del token**: nunca de algo que
 * el cliente pueda elegir. El guard decide qué se dibuja; **la autorización real
 * la hace el backend**, endpoint por endpoint, con la matriz de E2.4. Esconder
 * una ruta no la protege — si este guard fuera lo único, bastaría con escribir
 * la URL a mano.
 *
 * Un rol que llega a una ruta que no le corresponde ve **S-10** y no un 404: la
 * pantalla le dice que la sección no es parte de su rol y le da una salida. Un
 * 404 lo dejaría pensando que el enlace está roto.
 */

export type Yo = {
  usuarioId: string;
  rol: Rol;
  alias: string;
  tenantSlug: string;
};

/** Un enlace en la URL manda: si viene uno, se canjea ése y no se renueva nada. */
function hayEnlaceEnLaUrl(): boolean {
  return typeof window !== 'undefined' && /(^#|&)token=/.test(window.location.hash);
}

/**
 * Quién está usando la app.
 *
 * **Sin sesión, primero intenta el dispositivo recordado** (E3.24). Es lo que
 * hace que el paciente que cierra la app y vuelve a los tres días entre directo,
 * sin enlace: la sesión del navegador se fue con la pestaña, pero su teléfono
 * sigue recordado. Si no hay dispositivo (el equipo, o alguien que nunca entró
 * desde acá), la renovación contesta que no y todo sigue como siempre.
 */
export function useYo() {
  return useQuery({
    queryKey: ['yo'],
    queryFn: async () => {
      if (!leerSesion() && !hayEnlaceEnLaUrl()) await renovarConDispositivo();
      return obtener<Yo>('/api/v1/yo');
    },
    // La identidad no cambia mientras dure la sesión: no tiene sentido
    // revalidarla en cada montaje de una ruta.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

/**
 * Quién está usando la app, **y el reintento cuando vuelve la señal**.
 *
 * El mecanismo vive acá y no en cada pantalla porque hay dos que lo necesitan
 * —el guard y la entrada— y la pantalla que muestran promete, con todas las
 * letras, que se actualiza sola. Una promesa así no puede depender de que cada
 * lugar se acuerde de copiar el mismo `useEffect`, ni de que el valor por
 * defecto de una librería siga siendo el que es.
 *
 * `retry: false` en `useYo` deja el error puesto hasta que alguien lo vuelva a
 * pedir. Al volver la señal, lo pide esto: no hay botón que tocar ni recarga
 * que hacer.
 */
export function useYoConReintento() {
  const consulta = useYo();
  const { isError, error, refetch } = consulta;
  const sinRespuestaDelServidor = isError && error instanceof ErrorDeRed;

  useEffect(() => {
    if (!sinRespuestaDelServidor) return;
    const reintentar = () => void refetch();
    window.addEventListener('online', reintentar);
    return () => window.removeEventListener('online', reintentar);
  }, [sinRespuestaDelServidor, refetch]);

  return { ...consulta, sinRespuestaDelServidor };
}

/**
 * El servidor contestó, y contestó mal: un 5xx, o un cuerpo que no se pudo
 * leer. Se separa de un 4xx a propósito — un 403 o un 404 dicen algo sobre
 * quién pide, no sobre el sistema.
 */
function falloDelServidor(error: unknown): boolean {
  if (!(error instanceof ErrorDeApi)) return false;
  return error.estado >= 500 || error.codigo === 'respuesta_ilegible';
}

/** Paciente y cuidador vuelven con su dispositivo o con un enlace, nunca con contraseña. */
function entraSinContrasena(rol: Rol | undefined): boolean {
  return rol !== undefined && entraPorMagicLink(rol);
}

/** Dónde arranca cada rol al entrar. */
export const INICIO_DEL_ROL: Readonly<Record<Rol, string>> = Object.freeze({
  PACIENTE: '/inicio',
  CUIDADOR: '/cuidador',
  PROFESIONAL: '/equipo',
  COORDINADOR: '/equipo/operacion',
  ADMIN_INSTITUCION: '/equipo/operacion',
  // Fundación no ve pacientes de ninguna institución, ni siquiera agregados
  // (matriz E2.4; el reporte de red está diferido). Su panel es el del corpus y
  // la configuración, que llega con E4 y E5: hasta entonces tiene una entrada
  // propia que lo dice, en vez de una pantalla que le devolvería un 403.
  ADMIN_FUNDACION: '/fundacion',
});

/**
 * Deja pasar sólo a los roles indicados.
 *
 * Mientras la identidad carga muestra el spinner: dibujar la pantalla y sacarla
 * después sería peor que esperar.
 *
 * **Sin sesión y con la sesión vencida no son lo mismo** (H-24). Quien nunca
 * entró va al onboarding, que es la puerta del sistema. A quien **tenía** sesión
 * y el servidor le responde 401 se le dice lo que pasó: se cortó por
 * inactividad. Mandarlo al inicio, como hacía antes, le muestra "Para entrar
 * necesitás tu enlace" sobre un enlace que ya está gastado, y la persona no tiene
 * forma de entender que lo único que pasó fue que se le venció la sesión.
 *
 * El token vencido **no se borra**: si la persona recarga, tiene que volver a
 * leer lo mismo y no caer en la pantalla equivocada. El servidor lo rechaza
 * igual, así que guardarlo no abre nada.
 *
 * **Y "el servidor no contesta" tampoco es "no hay sesión"** (P-99). Un fallo de
 * red no llega como `ErrorDeApi` —no hubo respuesta que interpretar—, y hasta
 * E3.22 caía en el mismo `Navigate` que el de quien nunca entró: el paciente sin
 * señal terminaba leyendo "Para entrar necesitás tu enlace", sobre el enlace que
 * ya usó. Le decíamos que el problema era su acceso cuando era la señal, y es
 * justo lo contrario de lo que la app le promete al responder sin conexión. Con
 * sesión guardada se queda donde está, con la pantalla de S-08, y vuelve sola
 * cuando hay conexión. Y si el servidor **sí** contestó pero contestó mal —un
 * 500, un cuerpo ilegible—, tampoco es falta de sesión: es el estado de error,
 * con su "Reintentar". Sólo quien no tiene sesión guardada va a la entrada.
 */
export function ExigirRol({ roles, children }: { roles: readonly Rol[]; children: ReactNode }) {
  const {
    data: yo,
    isPending,
    isError,
    error,
    refetch,
    sinRespuestaDelServidor,
  } = useYoConReintento();
  const ubicacion = useLocation();

  if (isPending) return <Cargando mensaje={SISTEMA.cargando.sesion} />;

  if (isError || !yo) {
    const tieneSesion = leerSesion() !== null;

    if (error instanceof ErrorDeApi && error.exigeReingreso && tieneSesion) {
      // La sesión vencida es una pantalla sin sesión: lleva el aviso (DEMO-1).
      return (
        <>
          <AvisoDeDemostracion />
          <SesionExpirada entraConContrasena={!entraSinContrasena(leerSesion()?.rol)} />
        </>
      );
    }
    if (sinRespuestaDelServidor && tieneSesion) return <SinConexion />;
    // Hubo servidor y **falló el servidor** (un 500, un cuerpo ilegible).
    // Tampoco es "no hay sesión": la persona tiene la suya y lo que corresponde
    // es el estado de error, con su reintento. Un 4xx, en cambio, sigue yendo a
    // la entrada: quiere decir que esta persona no está donde cree que está
    // —todavía no terminó de darse de alta, por ejemplo— y ahí la entrada la
    // manda a donde va.
    if (falloDelServidor(error) && tieneSesion) {
      return <ErrorDeCarga onReintentar={() => void refetch()} />;
    }

    return <Navigate replace state={{ desde: ubicacion.pathname }} to="/" />;
  }

  if (!roles.includes(yo.rol)) {
    return <SinPermiso rol={SISTEMA.nombreDelRol[yo.rol]} />;
  }

  return <>{children}</>;
}
